/*
 * @Author: cheng
 * @Date: 2018-12-21 11:29:45
 * @Last Modified by: cheng
 * @Last Modified time: 2019-03-28 15:54:07
 */
import * as React from 'react';
import { cloneDeep, debounce, uniqueId } from 'lodash';
import { Input, message } from 'antd';
import { MenuConfig, ReferenceMenu, ReferenceMenuPropsConfig } from './reference-menu';
import { AutoCompleteMenu } from './auto-complete-menu';
import { getRangePosition, getTextLabelPositions, referenceUtils } from './utils';
import { API, defaultInputHeight } from '@config/index';
import {
  ParameterConfig,
  DefaultPropsConfig,
  PositionConfig,
  FunctionParameter
} from '@interface/index';
import { HotKeys } from '@lib/hotkeys';
import { isEqual, copyToClipboard, getAgentId, getUserId, getRawCodeTypeName, expandTypeName } from '@lib/index';
import textareaCaret from 'textarea-caret';
import '@style/component/reference-textarea.less';

import * as monaco from 'monaco-editor';
import { NiInput, niConfirm } from '..';
import { default as CommentInput } from './comment-input';

type Position = monaco.Position;
const MCompletionKind = monaco.languages.CompletionItemKind;

const TextArea = Input.TextArea;
function getIdeId() {
  return getAgentId() + getUserId();
}

enum BCompletionItemKind { // defined from backend
  TextCompletion = 1,
  MethodCompletion = 2,
  FunctionCompletion = 3,
  ConstructorCompletion = 4,
  FieldCompletion = 5,
  VariableCompletion = 6,
  ClassCompletion = 7,
  InterfaceCompletion = 8,
  ModuleCompletion = 9,
  PropertyCompletion = 10,
  UnitCompletion = 11,
  ValueCompletion = 12,
  EnumCompletion = 13,
  KeywordCompletion = 14,
  SnippetCompletion = 15,
  ColorCompletion = 16,
  FileCompletion = 17,
  ReferenceCompletion = 18,
  FolderCompletion = 19,
  EnumMemberCompletion = 20,
  ConstantCompletion = 21,
  StructCompletion = 22,
  EventCompletion = 23,
  OperatorCompletion = 24,
  TypeParameterCompletion = 25
}

function convertCompletionKind(res: string): monaco.languages.CompletionItemKind {
  switch (res) {
    case 'Text':
      return MCompletionKind.Text;
    case 'Method':
      return MCompletionKind.Method;
    case 'Function':
      return MCompletionKind.Function;
    case 'Constructor':
      return MCompletionKind.Constructor;
    case 'Field':
      return MCompletionKind.Field;
    case 'Variable':
      return MCompletionKind.Variable;
    case 'Class':
      return MCompletionKind.Class;
    case 'Interface':
      return MCompletionKind.Interface;
    case 'Module':
      return MCompletionKind.Module;
    case 'Property':
      return MCompletionKind.Property;
    case 'Unit':
      return MCompletionKind.Unit;
    case 'Value':
      return MCompletionKind.Value;
    case 'Enum':
      return MCompletionKind.Enum;
    case 'Keyword':
      return MCompletionKind.Keyword;
    case 'Snippet':
      return MCompletionKind.Snippet;
    case 'Color':
      return MCompletionKind.Color;
    case 'File':
      return MCompletionKind.File;
    case 'Reference':
      return MCompletionKind.Reference;
    case 'Folder':
      return MCompletionKind.Folder;
    case 'EnumMember':
      return MCompletionKind.EnumMember;
    case 'Constant':
      return MCompletionKind.Constant;
    case 'Struct':
      return MCompletionKind.Struct;
    case 'Event':
      return MCompletionKind.Event;
    case 'Operator':
      return MCompletionKind.Operator;
    case 'TypeParameter':
      return MCompletionKind.TypeParameter;
    default:
      return MCompletionKind.Text;
  }
}

export interface ReferenceUpdateConfig {
  nextReferenceData?: ReferenceConfig;
}

export interface ReferenceConfig {
  text: string;
  labels: Map<string, string[]>;
}

export type ValidataionResult = {
  valid: boolean;
  message?: string;
};

export enum ExprMode {
  condition = 'condition',
  filtering = 'filtering',
  otherExpr = 'other_expr'
}

export type ReferenceTextAreaPropsConfig = DefaultPropsConfig & {
  mode?: 'text' | undefined;
  ref?: any;
  rich?: boolean;
  rows?: number;
  autoSize?: boolean;
  disabled?: boolean;
  maxLength?: number;
  minLength?: number;
  name?: string;
  placeholder?: string;
  spellcheck?: boolean;
  readOnly?: boolean;
  data: string;
  initParameters: ParameterConfig[];
  bodyItTypeId?: string;
  forPSQL?: boolean;
  update: (next: string) => void;
  prefix?: string[];
  onEnter?: (value: string) => void;
  parameterMap: { [key: string]: ParameterConfig };
  allowNewLine?: boolean;
  forExpression?: boolean;
  rawCodeMode?: boolean;
  customSuggestions?: string[];
  onlyUseCustomSuggestion?: boolean;
  disableReferenceMode?: boolean;
  rawCodeType?: any,
  rawCodeLang?: string;
  rawCodeParams?: FunctionParameter[];
  rawCodeReturn?: any;
  rawCodeReceiver?: any
  rawCodeMethodName?: string;
  rawCodeExprMode?: ExprMode;
  resetOnEnter?: boolean;
  expressionValidation?: (expression: string) => ValidataionResult;
  forList?: boolean;
  hideComment?: boolean;
  comment?: string;
  onCommentChange?: (next: string) => void;
};

export interface ReferenceTextAreaStateConfig {
  tag: string;
  menuVisible: boolean;
  acMenuVisible: boolean;
  acTrigger: string;
  isParam: boolean;
  referenceData: ReferenceConfig;
  menuConf: MenuConfig;
}

const defaultState: ReferenceTextAreaStateConfig = {
  tag: '',
  menuVisible: false,
  acMenuVisible: false,
  acTrigger: '',
  isParam: true,
  referenceData: {
    text: '',
    labels: new Map()
  },
  menuConf: {
    rangeX: 0,
    rangeX2: 0,
    position: { x: 0, y: 0 }
  },
};

export class ReferenceTextArea extends React.Component<
  ReferenceTextAreaPropsConfig,
  ReferenceTextAreaStateConfig
> {
  $textAreaRef?: HTMLTextAreaElement;
  $acMenu?: AutoCompleteMenu;

  constructor(props: ReferenceTextAreaPropsConfig) {
    super(props);
    this.state = {
      ...defaultState,
    };
  }
  ideId = uniqueId('kotlin-ide-');

  ide: any;
  completion: any;

  componentDidMount() {
    this.onInit();
  }

  componentWillUnmount() {
    this.setState = () => {
      // do nothing
    };

    if (this.ide && this.ide.dispose) {
      this.ide.dispose();
    }

    if (this.completion) {
      this.completion.dispose();
    }
  }

  onInit = async () => {
    const props = this.props;

    const referenceData: any = await referenceUtils.toReference(
      props.data,
      props.parameterMap
    );

    this.setState({
      referenceData
    });

    setTimeout(() => {

      if (props.rawCodeMode && !props.disabled && !props.readOnly && !this.ide) {

        this.ide = monaco.editor.create(document.getElementById(this.ideId) as any, {
          value: props.data,
          language: props.rawCodeLang || 'kotlin',
          theme: 'vs',
          automaticLayout: true,
          minimap: {
            enabled: false
          },
          fontSize: 14,
          padding: { top: 12 },
          foldingStrategy: 'auto',
          unfoldOnClickAfterEndOfLine: true,
          scrollBeyondLastLine: false,
          lineNumbers: 'off',
          renderLineHighlight: 'none'
        });

        // this.ide.setValue(referenceData.text);

        this.ide.onDidChangeModelContent((event: any) => {

          this.setState(prevState => {
            const newValue = this.ide.getValue();
            const nextReferenceData = cloneDeep(prevState.referenceData);
            nextReferenceData.text = newValue;
            return { referenceData: nextReferenceData };
          });
        });

        this.ide.onDidFocusEditorText(() => {
          this.completion = monaco.languages.registerCompletionItemProvider('kotlin', {
            triggerCharacters: [
              '\n', '.', '(', '[', '{', '_', '-',
              // alphanumeric characters
              ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(i + 97)), // a-z
              ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(i + 65)), // A-Z
              ...Array.from({ length: 10 }, (_, i) => String(i)),                 // 0-9
            ],

            provideCompletionItems: async (model: any, position: Position, context: any) => {

              const text = model.getValue();

              const word = model.getWordUntilPosition(position);
              const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn
              };

              try {
                const res = await this.createDependencyProposals(range, this.ideId, text, position);
                return {
                  suggestions: res
                };
              } catch (e: any) {
                // do nothing
              }

              return {
                suggestions: []
              };
            }
          });
        });


        this.ide.onDidBlurEditorWidget((event: any) => {
          if (this.completion) {
            if (this.completion.dispose) {
              this.completion.dispose();
            }
            this.completion = undefined;
          }
          this.onBlur(event);
        });


      } else if (!(props.rawCodeMode && !props.disabled && !props.readOnly) && this.ide && this.ide.dispose) {
        this.ide.dispose();
        if (this.completion) {
          this.completion.dispose();
        }
      } else if (this.ide) {
        this.ide.setValue(referenceData.text);
      }

    }, 100);


    if (!this.props.rich && !/\n/g.test(props.data)) {
      setTimeout(() => {
        if (this.$textAreaRef) {
          this.$textAreaRef.style.height = 'auto';
          this.$textAreaRef.style.height = this.$textAreaRef.scrollHeight + 'px';
        }
      });
    }
  }

  componentWillMount(): void {
    if (this.completion) {
      this.completion.dispose();
    }

    if (this.ide && this.ide.dispose) {
      this.ide.dispose();
    }
  }

  componentDidUpdate(prevProps: any) {
    if (
      !isEqual(prevProps.disabled, this.props.disabled) ||
      !isEqual(prevProps.readOnly, this.props.readOnly)
    ) {
      this.onInit();
    }
  }

  render() {
    const {
      initParameters,
      className,
      mode,
      style,
      bodyItTypeId,
      forPSQL,
      forExpression,
      customSuggestions,
      onlyUseCustomSuggestion,
      rawCodeMode,
      rawCodeParams,
      rawCodeType,
      disabled,
      readOnly,
      allowNewLine,
      rows,
      comment,
      hideComment,
      onCommentChange
    } = this.props;
    const { tag, menuVisible, referenceData, menuConf, acTrigger, acMenuVisible } = this.state;

    const showIde = (rawCodeMode && !disabled && !readOnly);

    if (!referenceData) {
      return '';
    }
    const menuProps: ReferenceMenuPropsConfig = {
      tag,
      referenceData,
      menuConf,
      bodyItTypeId,
      forPSQL,
      forExpression,
      initParameters: cloneDeep(initParameters),
      update: this.handleUpdateLocal,
      visible: menuVisible,
      onRawCodeSelected: this.triggerRawCode,
      onFunctionParam: this.handleFunctionParam,
      close: this.triggerMenu.bind(this, false),
      textAreaRef: this.$textAreaRef
    };

    return (<>
      {
        showIde && rawCodeType && !hideComment &&
        <div key="comment">
          <CommentInput
            commentPayload={{
              comment: comment || '',
              ideId: this.ideId,
              typeName: getRawCodeTypeName(rawCodeType) || '',
              parameters: rawCodeParams && encodeURIComponent(JSON.stringify(rawCodeParams.map((item: FunctionParameter) => ({
                name: item.name,
                type: item.type && item.type.name,
                multi_value: item.multiValue,
                nullable: item.nullable
              }))))
            }}
            onCommentChange={(next: string) => {
              if (onCommentChange) {
                onCommentChange(next);
              }
            }}
            onCodeSelected={(code: string) => {

              if (referenceData.text && referenceData.text.length > 0) {


                niConfirm({
                  title: 'Confirm',
                  content: 'This operation will replace current code',
                  onOk: () => {

                    this.ide.setValue(code);
                    const nextReferenceData = cloneDeep(this.state.referenceData);
                    nextReferenceData.text = code;
                    this.setState({ referenceData: nextReferenceData }, this.onBlur);
                  }
                });
                return;
              }

              this.ide.setValue(code);
              const nextReferenceData = cloneDeep(this.state.referenceData);
              nextReferenceData.text = code;
              this.setState({ referenceData: nextReferenceData }, this.onBlur);

            }}
          />
        </div>
      }
      <div
        className={showIde ? 'framely-kotlin-ide' : `${className} reference-textarea__wrap`}
        id={this.ideId}
        style={{
          ...style
        }}
      >
        {
          showIde ? '' :
            (mode === 'text' ? (
              <span
                className="reference-textarea--mode-text"
              // style={this.props.style}
              >
                {referenceData.text}
              </span>
            ) : (
              <HotKeys
                keys={'shift+4,backspace,delete,command+z,ctrl+z,tab,return,up,down'}
                onKeyDown={(e: React.KeyboardEvent<HTMLElement>, handle: any) => {

                  switch (handle.shorcut) {
                    case 'delete':
                      this.handleDeleteHotKey(true);
                      break;
                    case 'backspace':
                      this.handleDeleteHotKey();
                      break;
                    case 'shift+4':
                      if (this.props.disableReferenceMode) {
                        return true;
                      }
                      this.handleShowMenu();
                      break;
                    case 'tab':
                      return false;
                    case 'return':
                      if (this.props.rich) {
                        return true;
                      }
                      if (acMenuVisible && this.$acMenu) {
                        this.$acMenu.handleHotKey(handle.shorcut);
                        return false;
                      }
                      return true;
                    case 'up':
                    case 'down':
                      if (acMenuVisible && this.$acMenu) {
                        this.$acMenu.handleHotKey(handle.shorcut);
                        return false;
                      }
                      return true;
                    default:
                      break;
                  }

                  return false;
                }}
              >
                <TextArea
                  // style={{ height: defaultInputHeight, ...this.props.style }}
                  disabled={this.props.disabled || this.props.disable}
                  data-tag={tag}
                  data-rows={this.props.rows || 1}
                  className={`reference-textarea${this.props.rich ? '-rich' : (this.props.forList ? '-for-list' : '')}`}
                  ref={this.getTextAreaRef}
                  rows={this.props.rows || 1}
                  value={referenceData.text}
                  onKeyDown={this.onKeyDown}
                  onChange={this.onChange}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  placeholder={this.props.placeholder}
                  autoSize={this.props.autoSize || this.props.rich}
                  maxLength={this.props.maxLength}
                  minLength={this.props.minLength}
                  name={this.props.name}
                  onCopy={(e: any) => {
                    e.preventDefault();
                    this.handleCopy();
                  }}
                  onCut={(e: any) => {
                    e.preventDefault();
                    this.handleCut();
                  }}
                  onPaste={(e: any) => {
                    e.preventDefault();
                    this.handlePaste(e.clipboardData.getData('Text'));
                  }}
                />
              </HotKeys>
            ))}

        {menuVisible ? <ReferenceMenu {...menuProps} /> : ''}
        {acMenuVisible
          ? <AutoCompleteMenu
            {
            ...menuProps
            }
            onRef={(ref: AutoCompleteMenu) => {
              this.$acMenu = ref;
            }}
            trigger={acTrigger}
            update={this.onACSelected}
            close={this.onACClosed}
            customSuggestions={customSuggestions}
            onlyUseCustomSuggestion={onlyUseCustomSuggestion}
          />
          : ''}
      </div>
    </>
    );
  }

  handleNewLine = () => {
    const { referenceData } = this.state;
    const next = cloneDeep(referenceData);
    if (!this.$textAreaRef) {
      return;
    }
    const range = getRangePosition(this.$textAreaRef);
    next.text = `${next.text.substring(0, range.rangeX)}\n${next.text.substring(range.rangeX2, next.text.length)}`;

    this.setState({
      referenceData: next
    }, () => {

      if (this.$textAreaRef) {
        this.$textAreaRef.selectionStart = range.rangeX + 1;
        this.$textAreaRef.selectionEnd = range.rangeX + 1;
      }
    });
  }

  handleDeleteHotKey = (isDeleteHotKey?: boolean) => {

    if (!this.$textAreaRef) {
      return;
    }
    const state = this.state;
    const props = this.props;

    const range = getRangePosition(this.$textAreaRef);

    const labelPositions = getTextLabelPositions(state.referenceData);

    let start = range.rangeX || 0;
    let end = range.rangeX2 || 0;

    if (start === 0 && end === 0 && !isDeleteHotKey) {
      return;
    }

    if (range.rangeX === range.rangeX2) {
      for (const position of labelPositions) {
        if (!isDeleteHotKey) {
          if (range.rangeX > position[0] && range.rangeX <= position[1] + 1) {
            start = position[0];
            end = position[1] + 1;
            break;
          }
        } else {
          if (range.rangeX > position[0] - 1 && range.rangeX <= position[1]) {
            start = position[0];
            end = position[1] + 1;
            break;
          }
        }
      }
    } else {
      for (const position of labelPositions) {
        if (range.rangeX >= position[0] && range.rangeX < position[1] + 1) {
          start = position[0];
        }

        if (
          range.rangeX2 &&
          range.rangeX2 > position[0] &&
          range.rangeX2 <= position[1] + 1
        ) {
          end = position[1] + 1;
        }
      }
    }

    range.rangeX = start;
    range.rangeX2 = end;

    const curValue = state.referenceData.text;
    if (this.$textAreaRef) {
      if (range.rangeX === range.rangeX2) {
        this.$textAreaRef.value =
          curValue.substring(0, isDeleteHotKey ? range.rangeX : range.rangeX - 1) +
          curValue.substring(isDeleteHotKey ? range.rangeX + 1 : range.rangeX, curValue.length);

        if (!isDeleteHotKey) {
          this.$textAreaRef.selectionStart = range.rangeX - 1;
          this.$textAreaRef.selectionEnd = range.rangeX - 1;
        } else {
          this.$textAreaRef.selectionStart = range.rangeX;
          this.$textAreaRef.selectionEnd = range.rangeX;
        }
      } else {
        this.$textAreaRef.value =
          curValue.substring(0, range.rangeX) +
          curValue.substring(range.rangeX2, curValue.length);
        this.$textAreaRef.selectionStart = range.rangeX;
        this.$textAreaRef.selectionEnd = range.rangeX;
      }

      if (
        state.referenceData.labels.has(
          curValue.substring(range.rangeX, range.rangeX2)
        )
      ) {
        state.referenceData.labels.delete(
          curValue.substring(range.rangeX, range.rangeX2)
        );
      }
      state.referenceData.text = this.$textAreaRef.value;
      this.$textAreaRef.dispatchEvent(new Event('Input'));
    }
    this.setState({
      referenceData: state.referenceData
    }, () => {
      // this.showAcMenuIfNeeded();
    });
  };

  handleShowMenu = () => {

    if (!this.$textAreaRef) {
      return;
    }
    const state = this.state;
    const props = this.props;
    const range = getRangePosition(this.$textAreaRef);

    const labelPositions = getTextLabelPositions(state.referenceData);
    let start = range.rangeX;
    let end = range.rangeX2;
    if (range.rangeX === range.rangeX2) {
      for (const position of labelPositions) {
        if (range.rangeX >= position[0] && range.rangeX < position[1] + 1) {
          start = position[0];
          end = position[1] + 1;
          break;
        }
      }
      if (start === 0) {
        start = range.rangeX;
        end = range.rangeX;
      }
    } else {
      for (const position of labelPositions) {
        if (range.rangeX >= position[0] && range.rangeX < position[1] + 1) {
          start = position[0];
        }

        if (
          range.rangeX2 &&
          range.rangeX2 > position[0] &&
          range.rangeX2 <= position[1] + 1
        ) {
          end = position[1] + 1;
        }
      }
    }

    range.rangeX = start;
    range.rangeX2 = start;
    const ref = state.referenceData;
    const text = ref.text;

    ref.text = `${text.substring(0, start)}$${text.substring(end, text.length)}`;
    this.setState({
      referenceData: ref,
      menuVisible: true,
      menuConf: {
        rangeX: range.rangeX + 1,
        rangeX2: range.rangeX2 + 1,
        position: this.getMenuPosition(range.rangeX)
      }
    });
  };

  getTextAreaRef = (ref: any) => {
    if (ref) {
      this.$textAreaRef = ref.textAreaRef || (ref.resizableTextArea && ref.resizableTextArea.textArea);
    }
  };

  triggerMenu = (visible: boolean) => {
    this.setState({
      menuVisible: visible
    });
  };

  onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const { referenceData, acMenuVisible } = this.state;
    const { onEnter, allowNewLine, resetOnEnter, rich } = this.props;

    // 无数据， enter 键禁止
    if (!referenceData) {
      e.preventDefault();

      return;
    }

    if (e.key === 'Enter' && !acMenuVisible) {
      if (e.ctrlKey || rich) {
        e.preventDefault();
        this.handleNewLine();
        return;
      }

      if (onEnter) {

        if (this.props.expressionValidation) {

          const ret = this.props.expressionValidation(referenceData.text);
          if (!ret.valid) {
            e.preventDefault();
            message.warning(ret.message);
            return;
          }
        }

        if (this.onBlur) {
          this.onBlur(e);
        }
        const nextReferenceData = cloneDeep(referenceData);
        const server = referenceUtils.toServer(nextReferenceData);
        onEnter(server);
        e.preventDefault();
      }

      if (!allowNewLine) {
        e.preventDefault();
      }
    }
  };

  getMenuPosition = (rangeX: number) => {

    if (!this.$textAreaRef) {
      return { x: 0, y: 0 };
    }

    const { referenceData } = this.state;
    const res: PositionConfig = {
      x: 0,
      y: 0
    };

    if (!referenceData) {
      return res;
    }

    const caret = textareaCaret(this.$textAreaRef, rangeX);
    res.x = caret.left + 20;
    res.y = caret.top + 20;

    const preRect: any = this.$textAreaRef.getBoundingClientRect();

    // 边界判断

    if (res.x < 0) {
      res.x = 0;
    }

    if (res.x > preRect.width - 250) {
      res.x = preRect.width - 250;
    }

    if (res.y < 0) {
      res.y = 0;
    }

    // document.body.removeChild(preEle);
    return res;
  };

  /**
   * 更新 text
   *
   * @memberof ReferenceTextArea
   */
  onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { referenceData } = this.state;
    const next = cloneDeep(referenceData);
    next.text = e.target.value;

    // setTimeout(() => {
    //   if (this.$textAreaRef) {
    //     this.$textAreaRef.style.height = 'auto';
    //     this.$textAreaRef.style.height = this.$textAreaRef.scrollHeight + 'px';
    //   }
    // });

    this.setState({
      referenceData: next
    });
  };

  /**
   * 聚焦时清除 menu
   *
   * @memberof ReferenceTextArea
   */
  onFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    this.setState({
      menuVisible: false
    });
  };

  /**
   * 失焦时调用 update 更新数据，不会做数据检查，请在提交的时候调用 regularData
   * 避免提交脏数据
   *
   * @memberof ReferenceTextArea
   */
  onBlur = (e?: any) => {
    const { rawCodeMode, disabled, readOnly, resetOnEnter } = this.props;
    const showIde = (rawCodeMode && !disabled && !readOnly);

    if (!showIde && e.relatedTarget && e.relatedTarget.className === 'reference-menu__container') {
      return;
    }

    const { menuVisible, acMenuVisible } = this.state;
    if (menuVisible) {
      return;
    }

    if (acMenuVisible) {
      this.setState({ acMenuVisible: false });
    }

    const { update, onEnter } = this.props;
    const { referenceData } = this.state;

    if (referenceData.text.length && this.props.expressionValidation) {

      const ret = this.props.expressionValidation(referenceData.text);
      if (!ret.valid) {
        if (e) {
          e.preventDefault();
        }
        message.warning(ret.message);
        return;
      }
    }

    const nextReferenceData = cloneDeep(referenceData);
    const server = referenceUtils.toServer(nextReferenceData);
    update(server);

    if (resetOnEnter) {
      this.setState({ referenceData: { text: '', labels: new Map() } });
    }
  };

  handleUpdateLocal = ({ nextReferenceData }: ReferenceUpdateConfig) => {

    if (!this.$textAreaRef) {
      return;
    }
    const { referenceData } = this.state;
    const { rangeX } = getRangePosition(this.$textAreaRef);
    this.setState({
      referenceData: nextReferenceData || referenceData
    });
  };


  getSelectedText = () => {
    const { referenceData } = this.state;

    let selectText = (window as any).getSelection().toString();
    const labels = referenceData.labels;

    for (const [k, v] of labels.entries()) {
      selectText = selectText.replace(k, `\$${v.join('.')}\$`);
    }

    return selectText;
  }

  handleCopy = () => {
    const selectText = this.getSelectedText();
    copyToClipboard(selectText);
  }

  handleCut = () => {
    if (!this.$textAreaRef) {
      return;
    }

    const { referenceData } = this.state;
    const selectText = this.getSelectedText();
    const range = getRangePosition(this.$textAreaRef);
    const oriText = referenceData.text;
    const nextText = oriText.substring(0, range.rangeX) + oriText.substring(range.rangeX2, oriText.length);
    referenceData.text = nextText;
    this.setState({ referenceData }, () => {
      copyToClipboard(selectText);
    });
  }

  handlePaste = async (text: string) => {
    if (!this.$textAreaRef) {
      return;
    }

    const { referenceData } = this.state;
    const newReferenceData = await referenceUtils.toReference(text, this.props.parameterMap);
    const range = getRangePosition(this.$textAreaRef);
    const oldText = referenceData.text;
    const addText = newReferenceData.text;


    const nextLabels = referenceData.labels;

    for (const [k, v] of newReferenceData.labels.entries()) {
      nextLabels.set(k, v);
    }

    const nextReferenceData = {
      text: `${oldText.substring(0, range.rangeX)
        }${addText
        }${oldText.substring(range.rangeX2, oldText.length)
        }`,
      labels: nextLabels
    };

    this.setState({
      referenceData: nextReferenceData
    });
  }

  triggerRawCode = (menuConf: MenuConfig) => {
    if (!this.$textAreaRef) {
      return;
    }
    const state = this.state;
    const props = this.props;
    const range = getRangePosition(this.$textAreaRef);


    const { referenceData } = this.state;
    const oldText = referenceData.text;
    const nextReferenceData = cloneDeep(referenceData);

    nextReferenceData.text = `${oldText.substring(0, menuConf.rangeX)}{}${oldText.substring(menuConf.rangeX2, oldText.length)}`;

    this.setState({ referenceData: nextReferenceData }, () => {

      setTimeout(() => {
        if (!this.$textAreaRef) {
          return;
        }

        this.$textAreaRef.setSelectionRange(menuConf.rangeX + 1, menuConf.rangeX + 1);
      });
    });
  }

  handleFunctionParam = (paramName: string, menuConf: MenuConfig) => {
    if (!this.$textAreaRef) {
      return;
    }
    const state = this.state;
    const props = this.props;
    const range = getRangePosition(this.$textAreaRef);


    const { referenceData } = this.state;
    const oldText = referenceData.text;
    const nextReferenceData = cloneDeep(referenceData);

    nextReferenceData.text = `${oldText.substring(0, menuConf.rangeX)}${paramName}_param\$${oldText.substring(menuConf.rangeX2, oldText.length)}`;

    this.setState({ referenceData: nextReferenceData }, () => {

      setTimeout(() => {
        if (!this.$textAreaRef) {
          return;
        }

        this.$textAreaRef.setSelectionRange(menuConf.rangeX + paramName.length + 7, menuConf.rangeX + paramName.length + 7);
      });
    });
  }

  showAcMenuIfNeeded = () => {

    if (!this.checkRawCodeMode()) {
      return;
    }

    const trigger = this.calTrigger();

    if (!trigger.length) {

      if (this.state.acMenuVisible) {
        this.setState({ acMenuVisible: false });
      }

      return;
    }

    if (!this.$textAreaRef) {
      return;
    }

    const range = getRangePosition(this.$textAreaRef);
    this.setState({
      menuConf: {
        rangeX: range.rangeX,
        rangeX2: range.rangeX,
        position: this.getMenuPosition(range.rangeX)
      },
      acTrigger: trigger,
      acMenuVisible: true
    });
  }

  checkRawCodeMode = () => {
    if (!this.$textAreaRef) {
      return false;
    }

    if (this.props.rawCodeMode) {
      return true;
    }

    const range = getRangePosition(this.$textAreaRef);

    const { referenceData } = this.state;

    const frontStr = referenceData.text.substring(0, range.rangeX);
    const endStr = referenceData.text.substring(range.rangeX);

    const fs = frontStr.lastIndexOf('${');
    const fe = frontStr.lastIndexOf('}');

    const es = endStr.indexOf('${');
    const ee = endStr.indexOf('}');

    if (fs !== -1 &&  // check if the caret position is in ${}
      fs > fe &&
      ee !== -1 &&
      (es === -1 || es > ee)
    ) {
      return true;
    }

    return false;
  }

  calTrigger = () => {
    if (!this.$textAreaRef) {
      return '';
    }
    const range = getRangePosition(this.$textAreaRef);

    const { referenceData } = this.state;

    const frontStr = referenceData.text.substring(0, range.rangeX);

    let trigger = '';

    const charRegex = /[0-9a-zA-Z_]$/;

    let i = frontStr.length - 1;

    for (i; i >= 0; i -= 1) {
      if (!charRegex.test(frontStr[i])) {
        break;
      }
    }

    trigger = frontStr.substring(i + 1);
    return trigger;
  }

  onACSelected = (value: string, index: number) => {
    const { acTrigger, referenceData } = this.state;

    if (!this.$textAreaRef) {
      return;
    }

    const range = getRangePosition(this.$textAreaRef);

    let nextText = referenceData.text;

    nextText = nextText.substring(0, range.rangeX - acTrigger.length) + value + nextText.substring(range.rangeX);
    const nextReferenceData = cloneDeep(referenceData);
    nextReferenceData.text = nextText;

    this.setState({
      acMenuVisible: false,
      referenceData: nextReferenceData
    }, () => {
      setTimeout(() => {
        if (!this.$textAreaRef) {
          return;
        }

        const nextIndex = range.rangeX - acTrigger.length + value.length;
        this.$textAreaRef.setSelectionRange(nextIndex, nextIndex);
      });
    });
  }

  onACClosed = () => {
    this.setState({ acMenuVisible: false, acTrigger: '' });
  }

  createDependencyProposals = async (range: any, ideId: string, text: string, position: monaco.Position) => {

    if (ideId !== this.ideId) {
      return [];
    }

    const { rawCodeType, rawCodeParams, rawCodeReturn, rawCodeReceiver, rawCodeMethodName, rawCodeExprMode } = this.props;

    try {
      const res: any = await API.common.getIdeSuggestions(
        ideId,
        encodeURIComponent(text),
        position.lineNumber,
        position.column,
        'kotlin',
        getRawCodeTypeName(rawCodeType),
        getRawCodeTypeName(rawCodeReturn),
        rawCodeParams && encodeURIComponent(JSON.stringify(rawCodeParams.map((item: FunctionParameter) => ({
          name: item.name,
          type: item.type && expandTypeName(item.type.name),
          multi_value: item.multiValue,
          nullable: item.nullable
        })))),
        getRawCodeTypeName(rawCodeReceiver),
        rawCodeMethodName,
        rawCodeExprMode
      );
      const suggestions: monaco.languages.CompletionItem[] = [];

      for (const s of res) {
        suggestions.push({
          range,
          label: s.label,
          kind: convertCompletionKind(s.kind),
          insertText: s.insertText || s.label,
          filterText: s.filterText,
          detail: s.detail,
          insertTextRules: s.insertTextFormat === 'Snippet' && monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet || undefined
        });
      }

      return suggestions;
    } catch (e: any) {
      throw e;
    }
  }
}
